import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { Button, ButtonToolbar, Panel } from 'react-bootstrap';
import Select from 'react-select';
import Layout from '../../components/layout/Layout';
import Constants from '../../config/constants';
import Card from '../../components/card/Card';
import DataTable from '../../components/datatable/DataTable';
import Alert from '../../modal/alert/Alert';
import Thumbnail from '../../components/thumbnail/Thumbnail';
import InputsRangeFilter from '../../components/filter/InputsRangeFilter';
import MaterialTransparentCreateModal from '../../modal/material_transparent/MaterialTransparentCreateModal';
import MaterialTransparentEditModal from '../../modal/material_transparent/MaterialTransparentEditModal';
import TransparentMaterialDuplicateModal from '../../modal/duplicate/TransparentMaterialDuplicateModal';

import { COLUMN_STYLE, getColumnStyle } from '../../components/datatable/util';
import { defaultRejectHandler, getErrorValue, toMilli, fromMilli, withDefaultSort } from '../../utils/utility';

import {
  clearErrorMaterialTransparent,
  listMaterialTransparent,
  deleteMaterialTransparent,
} from '../../redux/material_transparent/material_actions';

class MaterialTransparentPage extends React.Component {
  constructor(props) {
    super(props);
    this.search = {
      pageSize: Constants.DATA_TABLE_PAGE_SIZE,
      page: null,
      sorted: null,
      filtered: null,
    };

    const coefficientColumn = getColumnStyle(COLUMN_STYLE.coefficient);
    const { t } = this.props;

    this.columns = [
      {
        Header: '',
        accessor: 'thumbnail',
        Cell: row => <Thumbnail img={row.value} />,
        ...getColumnStyle(COLUMN_STYLE.thumbnail),
      },
      {
        Header: () => <b>{t('name')}</b>,
        accessor: 'name',
        ...getColumnStyle(COLUMN_STYLE.text),
      },
      {
        Header: () => <b>{t('category')}</b>,
        accessor: 'is_public',
        Cell: row => row.value
          ? <span><i className="fas fa-users"/> {t('public')}</span>
          : <span><i className="fas fa-user"/> {t('private')}</span>,
        ...getColumnStyle(COLUMN_STYLE.category),
      },
      {
        id: 'thickness',
        Header: () => <b>{t('thickness')}<br />({t('unit_mm')})</b>,
        accessor: data => toMilli(data.thickness),
        ...coefficientColumn,
        width: 140,
      },
      {
        Header: () => <b>{t('U-value')}<br />({t('W/sq_m_C')})</b>,
        accessor: 'U',
        ...coefficientColumn,
        width: 160,
      },
      {
        Header: () => <b>{t('SHGC')}</b>,
        accessor: 'SHGC',
        ...coefficientColumn,
        width: 100,
      },
      {
        Header: () => <b>{t('visible transmittance')}</b>,
        accessor: 'transmittance',
        ...coefficientColumn,
        width: 120,
      },
      {
        Header: () => <b>{t('cfp')}<br />({t('kgCO2eq per unit')})</b>,
        accessor: 'cfp_value',
        Cell: row => `${Number(row.value || 0)} ${row.original.cfp_unit || ''}`,
        ...coefficientColumn,
        width: 120,
      },
    ];

    this.state = {
      selectedMaterial: null,
      categoryOption: 'all',
      isShowCreateModal: false,
      isShowEditModal: false,
      isShowDeleteModal: false,
      isShowDuplicateModal: false,
      isShowFilter: false,
      isCreated: false,
      isEdited: false,
      isDuplicated: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.isCreating && nextProps.isCreating !== this.props.isCreating && !nextProps.errorMessage) {
      this.setState({ isShowCreateModal: false, isCreated: true });
    }

    if (this.props.isEditing && nextProps.isEditing !== this.props.isEditing && !nextProps.errorMessage) {
      this.setState({ isShowEditModal: false, isEdited: true  });
    }

    if (this.props.isDeleting && nextProps.isDeleting !== this.props.isDeleting && !nextProps.errorMessage) {
      this.setState({ isShowDeleteModal: false });
    }

    if (this.props.isDuplicating && nextProps.isDuplicating !== this.props.isDuplicating && !nextProps.errorMessage) {
      this.setState({ isShowDuplicateModal: false, isDuplicated: true });
    }
  }

  renderActionMenu() {
    const { t } = this.props;
    return [
      <li>
        <a
          id="material-transparent-create"
          href="#"
          data-toggle="modal"
          data-target="#create-material-transparent"
          onClick={() => this.setState({ isShowCreateModal: true, isCreated: false  })}
        >
          <i className="fas fa-plus-square" />
          <span className="menu-text">{t('create transparent material')}</span>
        </a>
      </li>,
    ];
  }

  updateSortFilter(search, filter, state, category) {
    if (state) {
      this.search.pageSize = state.pageSize;
      this.search.page = state.page;
      this.search.sorted = state.sorted;
    }
    this.search.search = search;
    if (filter !== undefined) {
      this.search.filtered = {
        ...this.search.filtered,
        ...filter,
      };
    }

    this.props.listMaterial(
      this.search.pageSize,
      this.search.page,
      withDefaultSort(this.search.sorted),
      this.search.search,
      this.search.filtered,
      {
        all: null,
        public: true,
        private: false,
      }[category],
    );

    this.setState({ categoryOption: category });
  }

  dismissCreateModal() {
    this.props.clearError();
    this.setState({ isShowCreateModal: false });
  }

  dismissEditModal() {
    this.props.clearError();
    this.setState({ isShowEditModal: false });
  }

  dismissDeleteModal() {
    this.props.clearError();
    this.setState({ isShowDeleteModal: false });
  }

  dismissDuplicateModal() {
    this.props.clearError();
    this.setState({ isShowDuplicateModal: false });
  }

  render() {
    const {
      categoryOption,
      selectedMaterial: material,
      isShowCreateModal,
      isShowEditModal,
      isShowDeleteModal,
      isShowDuplicateModal,
    } = this.state;

    const {
      history,
      materials,
      filteredCount,
      isLoading,
      isDeleting,
      errorMessage,
      t,
    } = this.props;

    const breadcrumbItems = [
      { name: t('materials'), path: '/materials' },
      { name: t('transparent material'), path: '/materials/transparent' },
    ];

    const helpConfig = {
      title: t('help'),
      contentUrl: '/html/help_transparent_material.html',
    };

    return (
      <Layout
        history={history}
        breadcrumbItems={breadcrumbItems}
        renderActionMenu={() => this.renderActionMenu()}
        help={helpConfig}
      >
        <MaterialTransparentCreateModal
          show={isShowCreateModal}
          isCreated={this.state.isCreated}
          onHide={() => this.dismissCreateModal()}
        />
        <MaterialTransparentEditModal
          material={material}
          show={isShowEditModal}
          isEdited={this.state.isEdited}
          onHide={() => this.dismissEditModal()}
        />
        <TransparentMaterialDuplicateModal
          item={material}
          show={isShowDuplicateModal}
          title={t('copy transparent material')}
          label={t('new transparent material name')}
          isDuplicated={this.state.isDuplicated}
          onHide={() => this.dismissDuplicateModal()}
        />
        <Alert
          show={isShowDeleteModal}
          onHide={() => !isDeleting && this.dismissDeleteModal()}
          title={t('delete material')}
          body={`${t('do you want to delete')} ${material ? material.name : ''}?`}
          errorMessage={getErrorValue(errorMessage, 'detail')}
          primaryButtonTitle={isDeleting ? `${t('deleting')}...` : t('delete')}
          primaryButtonStyle="danger"
          primaryButtonEnabled={!isDeleting}
          onPrimaryButtonClick={() => this.props.deleteMaterial(material)}
        />
        <h1>{t('transparent material')}</h1>
        <Card padding={false}>
          <div className="filter-box" style={{ display: 'flex', padding: 20, marginBottom: 20 }}>
            <input
              className="text-search"
              id="material-transparent-search-name"
              style={{ width: 240, height: 36, margin: 10 }}
              type="text"
              onChange={event => this.updateSortFilter(event.target.value, undefined, null, this.state.categoryOption)}
              placeholder={t('search name')}
            />
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              style={{ width: 160, margin: 10 }}
              value={this.state.categoryOption}
              placeholder={t('category')}
              onChange={option => this.updateSortFilter(this.search.search, this.search.filtered, null, option.value)}
              options={[
                { value: 'all', label: t('all') },
                { value: 'public', label: t('public') },
                { value: 'private', label: t('private') },
              ]}
            />
            <ButtonToolbar style={{ width: 160, marginLeft: 10, marginRight: 10, marginBottom: 10 }}>
              <Button style={{ height: '100%'}} onClick={() => this.setState({ isShowFilter: !this.state.isShowFilter })}>
                {t('filter')}
              </Button>
            </ButtonToolbar>
          </div>
          <Panel id="filter-panel" expanded={this.state.isShowFilter}>
            <Panel.Collapse>
              <Panel.Body>
                <div className="filter-box" style={{ display: 'flex' }}>
                  <InputsRangeFilter
                    style={{ marginLeft: 10, marginRight: 10 }}
                    title={`${t('thickness')} (${t('unit_mm')})`}
                    minPlaceholder="3"
                    maxPlaceholder="12"
                    onApplyFilter={(min, max) => this.updateSortFilter(this.search.search, ({thickness: {min: fromMilli(min), max: fromMilli(max)}}),null, categoryOption)}
                  />
                  <InputsRangeFilter
                    style={{ marginLeft: 10, marginRight: 10 }}
                    title={`${t('U-value')} (${t('W/sq_m_C')})`}
                    minPlaceholder="2.4"
                    maxPlaceholder="5.1"
                    onApplyFilter={(min, max) => this.updateSortFilter(this.search.search, ({U: {min, max}}),null, categoryOption)}
                  />
                  <InputsRangeFilter
                    style={{ marginLeft: 10, marginRight: 10 }}
                    title={`${t('SHGC')}`}
                    minPlaceholder="0.1"
                    maxPlaceholder="4.7"
                    onApplyFilter={(min, max) => this.updateSortFilter(this.search.search, ({SHGC: {min, max}}),null, categoryOption)}
                  />
                  <InputsRangeFilter
                    style={{ marginLeft: 10, marginRight: 10 }}
                    title={`${t('visible transmittance')}`}
                    minPlaceholder="0.7"
                    maxPlaceholder="3.0"
                    onApplyFilter={(min, max) => this.updateSortFilter(this.search.search, ({transmittance: {min, max}}),null, categoryOption)}
                  />
                </div>
              </Panel.Body>
            </Panel.Collapse>
          </Panel>
          <DataTable
            columns={this.columns}
            data={materials}
            onActionEdit={item => this.setState({ isShowEditModal: true, selectedMaterial: item.original, isEdited: false })}
            onActionDuplicate={item => this.setState({ isShowDuplicateModal: true, selectedMaterial: item.original, isDuplicated: false })}
            onActionDelete={item => this.setState({ isShowDeleteModal: true, selectedMaterial: item.original })}
            filteredCount={filteredCount}
            isLoading={isLoading}
            onFetchData={state => this.updateSortFilter(this.search.search, this.search.filtered, state, this.state.categoryOption)}
          />
        </Card>
      </Layout>
    );
  }
}

const mapStateToProps = state => ({
  materials: state.material.transparent.orderedIds.map(id => state.material.transparent.materials[id]),
  filteredCount: state.material.transparent.filteredCount,
  isLoading: state.material.transparent.isLoading,
  isCreating: state.material.transparent.isCreating,
  isEditing: state.material.transparent.isEditing,
  isDeleting: state.material.transparent.isDeleting,
  isDuplicating: state.material.transparent.isDuplicating,
  errorMessage: state.material.transparent.errorMessage,
});

const mapDispatchToProps = dispatch => ({
  listMaterial: (pageSize, page, sorted, search, filtered, isPublic) =>
    dispatch(listMaterialTransparent(pageSize, page, sorted, search, filtered, isPublic)),
  deleteMaterial: material => defaultRejectHandler(dispatch(deleteMaterialTransparent(material.id))),
  clearError: () => dispatch(clearErrorMaterialTransparent()),
});

export default translate('translations')(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(MaterialTransparentPage)
);



// WEBPACK FOOTER //
// ./src/pages/material_transparent/MaterialTransparentPage.js